import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, MapPinned, Wrench, CalendarDays, Info, MapPin, Ruler, Package, Clock, PauseCircle, Power, Loader2, ChevronDown, MessageCircle, PoundSterling } from "lucide-react";
import { toast } from "sonner";
import PublicHeader from "@/components/PublicHeader";
import { FitterJobCard } from "@/components/FitterJobCard";
import { api, floorLabel, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUnit } from "@/context/UnitContext";
import { dimPairLabel } from "@/lib/units";
import { SERVICE_LABEL, JOB_TYPE_LABEL, AVAILABILITY_OPTIONS, RADIUS_OPTIONS, FITTER_SERVICES, FITTER_SERVICE_CHOICES } from "@/lib/fitters";

const AVAIL_LABEL = Object.fromEntries(AVAILABILITY_OPTIONS.map((a) => [a.value, a.label]));
const JOB_TYPE_STYLE = {
  collection: "bg-sky-100 text-sky-800",
  fitting: "bg-violet-100 text-violet-800",
  collection_fitting: "bg-emerald-100 text-emerald-800",
  delivery: "bg-amber-100 text-amber-800",
  measuring: "bg-teal-100 text-teal-800",
};
const STATUS_LABEL = { approved: "Approved", paused: "Paused", contacted: "In review", new: "In review" };

export default function FitterDashboard() {
  const { user, loading } = useAuth();
  const [mode, setMode] = useState("loading"); // loading | real | demo
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("available");
  const [busyId, setBusyId] = useState(null);

  const loadDemo = useCallback(() => {
    api.get("/fitters/dashboard").then((r) => {
      setData(r.data);
      setMode("demo");
    }).catch(() => setMode("demo"));
  }, []);

  const loadReal = useCallback(() => {
    return api.get("/fitter/opportunities")
      .then((r) => { setData(r.data); setMode("real"); return true; })
      .catch(() => { loadDemo(); return false; });
  }, [loadDemo]);

  useEffect(() => {
    if (loading) return;
    if (user) loadReal();
    else loadDemo();
  }, [user, loading, loadReal, loadDemo]);

  async function act(id, verb, successMsg) {
    setBusyId(id);
    try {
      await api.post(`/fitter/opportunities/${id}/${verb}`);
      toast.success(successMsg);
      await loadReal();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  }

  if (mode === "loading") {
    return (
      <>
        <PublicHeader />
        <main className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500" data-testid="fitter-dashboard-loading">
          Loading your dashboard…
        </main>
      </>
    );
  }

  const isReal = mode === "real";
  const p = data?.profile || {};
  const jobs = isReal
    ? { available: data?.available || [], accepted: data?.accepted || [], completed: data?.completed || [] }
    : { available: data?.available_jobs || [], accepted: data?.accepted_jobs || [], completed: data?.completed_jobs || [] };

  const paused = isReal && p.status === "paused";

  return (
    <>
      <PublicHeader />
      <main className="min-h-screen bg-slate-50 pb-24" data-testid="fitter-dashboard-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
          {!isReal && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm flex items-start gap-2 mb-5" data-testid="demo-banner">
              <Info className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                This is a <strong>demonstration dashboard</strong>. Jobs shown are examples only —
                accepting them creates no real booking. {user ? "Approved fitters see live opportunities here." : "Sign in with your approved fitter account to see live opportunities."}
              </span>
            </div>
          )}

          {paused && (
            <div className="bg-slate-100 border border-slate-300 text-slate-700 rounded-xl px-4 py-3 text-sm flex items-start gap-2 mb-5" data-testid="paused-banner">
              <PauseCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                Your account is <strong>paused</strong>, so you won't be offered new jobs right now.
                You can still see and complete work you've already accepted. Contact FloorMatch to resume.
              </span>
            </div>
          )}

          {/* Profile */}
          <div className="bg-[#0F3D2E] text-white rounded-2xl p-5 sm:p-6" data-testid="fitter-profile-card">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-display font-bold text-xl text-white">{p.display_name || "Fitter"}</h1>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-200 inline-flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> {STATUS_LABEL[p.status] || p.status || "Fitter"}{!isReal ? " (demo)" : ""}
                  </span>
                </div>
                <p className="text-white/70 text-sm mt-1 flex items-center gap-1">
                  <MapPinned className="w-3.5 h-3.5" /> Working radius: {p.working_radius_miles} miles
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <div className="bg-white/10 border border-white/15 rounded-xl p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-[#9fe0b6] flex items-center gap-1">
                  <Wrench className="w-3.5 h-3.5" /> Services offered
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(p.services || []).map((s) => (
                    <span key={s} className="text-xs bg-white/10 rounded-full px-2.5 py-1">{SERVICE_LABEL[s] || s}</span>
                  ))}
                </div>
              </div>
              <div className="bg-white/10 border border-white/15 rounded-xl p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-[#9fe0b6] flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5" /> Availability
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(p.availability || []).map((a) => (
                    <span key={a} className="text-xs bg-white/10 rounded-full px-2.5 py-1">{AVAIL_LABEL[a] || a}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {isReal && <ProfileControls profile={p} onSaved={loadReal} />}

          {/* Tabs */}
          <div className="flex gap-1 mt-6 bg-white border border-slate-200 rounded-full p-1 w-full sm:w-auto" data-testid="fitter-tabs">
            {[
              { k: "available", l: `Available (${jobs.available.length})` },
              { k: "accepted", l: `Accepted (${jobs.accepted.length})` },
              { k: "completed", l: `Completed (${jobs.completed.length})` },
            ].map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)}
                className={`flex-1 sm:flex-none px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.k ? "bg-[#E05915] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                data-testid={`tab-${t.k}`}>
                {t.l}
              </button>
            ))}
          </div>

          {/* Jobs */}
          <div className="grid md:grid-cols-2 gap-4 mt-5" data-testid="fitter-jobs">
            {jobs[tab].length === 0 ? (
              <div className="col-span-full bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500" data-testid="fitter-jobs-empty">
                {tab === "available" ? (isReal && paused ? "You won't be offered new jobs while paused." : "No opportunities right now — we'll notify you when a nearby job lands.") : `No ${tab} jobs yet.`}
              </div>
            ) : isReal ? (
              jobs[tab].map((job) => (
                <RealOppCard
                  key={job.id}
                  job={job}
                  tab={tab}
                  busy={busyId === job.id}
                  onAccept={() => act(job.id, "accept", "Accepted — we'll be in touch to arrange the details.")}
                  onDecline={() => act(job.id, "decline", "Declined. It stays open for other fitters.")}
                  onComplete={() => act(job.id, "complete", "Marked as completed.")}
                />
              ))
            ) : (
              jobs[tab].map((job) => (
                <FitterJobCard
                  key={job.id}
                  job={job}
                  onView={() => toast.info("Demo job — full details unlock once a real job is assigned.")}
                  onAccept={() => toast.success("Demo only — no real booking created.")}
                  acceptLabel="Accept (demo)"
                />
              ))
            )}
          </div>

          {!isReal && (
            <div className="text-center mt-8">
              <Link to="/fitters/join" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#0F3D2E] text-white font-semibold text-sm" data-testid="dashboard-join-btn">
                Join the founding fitter network
              </Link>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

function ProfileControls({ profile, onSaved }) {
  const [accepting, setAccepting] = useState(profile.accepting_work !== false);
  const [radius, setRadius] = useState(profile.working_radius_miles || 15);
  const [services, setServices] = useState(profile.services || []);
  const [smsOptIn, setSmsOptIn] = useState(!!profile.sms_opt_in);
  const [savingSms, setSavingSms] = useState(false);
  const [open, setOpen] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    setAccepting(profile.accepting_work !== false);
    setRadius(profile.working_radius_miles || 15);
    setServices(profile.services || []);
    setSmsOptIn(!!profile.sms_opt_in);
  }, [profile]);

  async function toggleSms() {
    const next = !smsOptIn;
    setSmsOptIn(next);
    setSavingSms(true);
    try {
      await api.patch("/fitter/me", { sms_opt_in: next });
      toast.success(next ? "Text alerts on — we'll text you about nearby jobs" : "Text alerts off");
      await onSaved();
    } catch (e) {
      setSmsOptIn(!next);
      toast.error(formatApiError(e));
    } finally {
      setSavingSms(false);
    }
  }

  async function toggleAccepting() {
    const next = !accepting;
    setAccepting(next);
    setSavingToggle(true);
    try {
      await api.patch("/fitter/me", { accepting_work: next });
      toast.success(next ? "You're now accepting new work" : "Paused new offers — you'll still see accepted jobs");
      await onSaved();
    } catch (e) {
      setAccepting(!next);
      toast.error(formatApiError(e));
    } finally {
      setSavingToggle(false);
    }
  }

  function toggleService(v) {
    setServices((prev) => (prev.includes(v) ? prev.filter((s) => s !== v) : [...prev, v]));
  }

  async function saveProfile() {
    setSavingProfile(true);
    try {
      await api.patch("/fitter/me", { working_radius_miles: Number(radius), services });
      toast.success("Profile updated");
      setOpen(false);
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingProfile(false);
    }
  }

  return (
    <div className="mt-3 bg-white border border-slate-200 rounded-2xl p-4 sm:p-5" data-testid="fitter-profile-controls">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <Power className={`w-4 h-4 ${accepting ? "text-emerald-600" : "text-slate-400"}`} />
            Accepting new work
          </div>
          <p className="text-sm text-slate-500 mt-0.5">{accepting ? "You're visible for new nearby jobs." : "You won't be offered new jobs. Accepted work is unaffected."}</p>
        </div>
        <button
          onClick={toggleAccepting}
          disabled={savingToggle}
          role="switch"
          aria-checked={accepting}
          className={`relative shrink-0 w-14 h-8 rounded-full transition-colors disabled:opacity-60 ${accepting ? "bg-emerald-500" : "bg-slate-300"}`}
          data-testid="accepting-work-toggle"
        >
          <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${accepting ? "translate-x-6" : "translate-x-0"}`} />
        </button>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
        <div className="min-w-0">
          <div className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <MessageCircle className={`w-4 h-4 ${smsOptIn ? "text-emerald-600" : "text-slate-400"}`} />
            Text me about new jobs
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            {profile.has_mobile === false
              ? "Add a mobile number to your profile to enable texts."
              : smsOptIn
              ? "We'll text you the moment a matching job comes in."
              : "Get a text alert for nearby jobs so you never miss one."}
          </p>
        </div>
        <button
          onClick={toggleSms}
          disabled={savingSms || profile.has_mobile === false}
          role="switch"
          aria-checked={smsOptIn}
          className={`relative shrink-0 w-14 h-8 rounded-full transition-colors disabled:opacity-60 ${smsOptIn ? "bg-emerald-500" : "bg-slate-300"}`}
          data-testid="sms-opt-in-toggle"
        >
          <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${smsOptIn ? "translate-x-6" : "translate-x-0"}`} />
        </button>
      </div>

      <button
        onClick={() => setOpen((o) => !o)}
        className="mt-4 w-full flex items-center justify-between gap-2 text-sm font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100 rounded-xl px-4 py-2.5 transition-colors"
        data-testid="edit-fitter-profile-toggle"
      >
        <span className="inline-flex items-center gap-2"><Wrench className="w-4 h-4" /> Edit working area &amp; skills</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-4 space-y-4" data-testid="fitter-profile-editor">
          <label className="block">
            <span className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-1.5"><MapPinned className="w-3.5 h-3.5" /> Working radius (miles)</span>
            <select
              value={radius}
              onChange={(e) => setRadius(e.target.value)}
              className="w-full h-11 rounded-md border border-slate-300 px-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
              data-testid="fitter-radius-select"
            >
              {RADIUS_OPTIONS.map((r) => <option key={r} value={r}>{r} miles</option>)}
            </select>
          </label>

          <div>
            <span className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-2"><Wrench className="w-3.5 h-3.5" /> Flooring skills &amp; services</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {FITTER_SERVICE_CHOICES.map((s) => {
                const on = services.includes(s.value);
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => toggleService(s.value)}
                    className={`text-left text-sm rounded-lg border px-3 py-2 transition-colors ${on ? "border-[#0F3D2E] bg-[#0F3D2E]/5 text-[#0F3D2E] font-semibold" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`fitter-skill-${s.value}`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            onClick={saveProfile}
            disabled={savingProfile}
            className="w-full h-11 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="save-fitter-profile"
          >
            {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save changes
          </button>
        </div>
      )}
    </div>
  );
}

function RealOppCard({ job, tab, busy, onAccept, onDecline, onComplete }) {
  const { unit } = useUnit();
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`opp-card-${job.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full ${JOB_TYPE_STYLE[job.job_type] || "bg-slate-100 text-slate-700"}`}>
            {JOB_TYPE_LABEL[job.job_type] || job.job_type}
          </span>
          <h3 className="font-display font-semibold text-lg text-slate-900 mt-2">{job.title}</h3>
          <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
            <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
            {job.locality} · {job.distance_bucket || (job.distance_miles != null ? `approx ${job.distance_miles} miles away` : "nearby")}
          </p>
        </div>
        <span className="text-[11px] font-mono text-slate-400 shrink-0">{job.reference_code}</span>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
        <Fact icon={Package} label="Flooring" value={floorLabel(job.flooring_type) || "—"} />
        {job.room_width_m && job.room_length_m ? (
          <Fact icon={Ruler} label="Room" value={dimPairLabel(job.room_width_m, job.room_length_m, unit)} />
        ) : job.total_area_m2 ? (
          <Fact icon={Ruler} label="Total area" value={`${job.total_area_m2} m²`} />
        ) : null}
      </div>

      {(job.rooms || []).length > 1 && (
        <p className="mt-3 text-xs text-slate-500">{job.rooms.length} rooms in this job.</p>
      )}

      {job.delivery_fee_gbp != null && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F3D2E] bg-[#2A5A3B]/10 rounded-full px-3 py-1" data-testid={`opp-delivery-fee-${job.id}`}>
          <PoundSterling className="w-3.5 h-3.5" aria-hidden="true" />
          Delivery fee £{Number(job.delivery_fee_gbp).toFixed(2)} — paid directly to you
        </div>
      )}

      {(job.services_required || []).length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Services required</p>
          <div className="flex flex-wrap gap-1.5">
            {job.services_required.map((s) => (
              <span key={s} className="text-xs bg-slate-100 text-slate-700 rounded-full px-2.5 py-1">{SERVICE_LABEL[s] || s}</span>
            ))}
          </div>
        </div>
      )}

      {(job.availability_text || []).length > 0 && (
        <div className="mt-4 flex items-center gap-2 text-sm text-slate-600">
          <CalendarDays className="w-4 h-4 text-slate-400" aria-hidden="true" />
          Customer availability: <strong>{job.availability_text.join(" or ")}</strong>
        </div>
      )}

      {job.note && <p className="mt-3 text-sm text-slate-600 italic">“{job.note}”</p>}

      {(job.room_photo_urls || []).length > 0 && (
        <div className="mt-4" data-testid={`opp-room-photos-${job.id}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Room photos</p>
          <div className="flex flex-wrap gap-2">
            {job.room_photo_urls.map((p, i) => (
              <a
                key={p + i}
                href={p}
                target="_blank"
                rel="noreferrer"
                className="block w-20 h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-100"
                data-testid={`opp-room-photo-${job.id}-${i}`}
              >
                <img src={p} alt={`Room photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </a>
            ))}
          </div>
        </div>
      )}

      <p className="mt-3 text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 flex items-start gap-1.5">
        <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
        The customer's identity and exact address stay hidden until the job is confirmed. Accepting registers your interest — FloorMatch coordinates the dates and price.
      </p>

      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        {tab === "available" && (
          <>
            <button onClick={onDecline} disabled={busy}
              className="flex-1 h-11 rounded-full border border-slate-300 bg-white hover:bg-slate-50 font-semibold text-sm text-slate-800 transition-colors disabled:opacity-50"
              data-testid={`decline-opp-${job.id}`}>
              Decline
            </button>
            <button onClick={onAccept} disabled={busy}
              className="flex-1 h-11 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold text-sm transition-colors disabled:opacity-50"
              data-testid={`accept-opp-${job.id}`}>
              {busy ? "…" : "Accept job"}
            </button>
          </>
        )}
        {tab === "accepted" && (
          <button onClick={onComplete} disabled={busy}
            className="flex-1 h-11 rounded-full bg-[#0F3D2E] hover:bg-[#0c3326] text-white font-semibold text-sm transition-colors disabled:opacity-50"
            data-testid={`complete-opp-${job.id}`}>
            {busy ? "…" : "Mark as completed"}
          </button>
        )}
        {tab === "completed" && (
          <span className="flex-1 h-11 inline-flex items-center justify-center rounded-full bg-emerald-50 text-emerald-700 font-semibold text-sm border border-emerald-100">
            Completed
          </span>
        )}
      </div>
    </div>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
      <div className="text-[11px] text-slate-500 flex items-center gap-1">
        <Icon className="w-3 h-3" aria-hidden="true" /> {label}
      </div>
      <div className="font-semibold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
